/**
 * Token Manager for Auth0 authentication
 * Handles token caching and prevents race conditions
 * Provides a singleton pattern for token management
 */

class TokenManager {
  constructor() {
    this.tokenPromise = null
    this.cachedToken = null
    this.tokenExpiry = null
    this.getAccessTokenSilently = null
    // Track an in-flight refresh to avoid concurrent refresh storms
    this.refreshPromise = null
  }

  /**
   * Initialize the token manager with Auth0 hook
   * This should be called from a React component
   */
  initialize(getAccessTokenSilently) {
    this.getAccessTokenSilently = getAccessTokenSilently
  }

  /**
   * Get access token with caching and race condition prevention
   * @param {Object} options - Auth0 options for token retrieval
   * @returns {Promise<string>} Access token
   */
  async getToken(options = {}) {
    // If we already have a valid cached token, return it
    if (this.cachedToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.cachedToken
    }

    // If there's already a token request in progress, wait for it
    if (this.tokenPromise) {
      return this.tokenPromise
    }

    // Start new token request
    this.tokenPromise = this._fetchToken(options)
    
    try {
      const token = await this.tokenPromise
      return token
    } finally {
      // Clear the promise so future requests can start fresh
      this.tokenPromise = null
    }
  }

  /**
   * Fetch token from Auth0
   * @private
   */
  async _fetchToken(options) {
    if (!this.getAccessTokenSilently) {
      throw new Error('TokenManager not initialized. Call initialize() first.')
    }

    try {
      const token = await this.getAccessTokenSilently({
        audience: 'https://api.startuptnt.com',
        ...options
      })

      // Cache the token with 5-minute buffer before expiry
      this.cachedToken = token
      this.tokenExpiry = Date.now() + (55 * 60 * 1000) // 55 minutes

      return token
    } catch (error) {
      console.error('Failed to get access token:', error)
      throw error
    }
  }

  /**
   * Clear cached token (useful for logout)
   */
  clearToken() {
    this.cachedToken = null
    this.tokenExpiry = null
    this.tokenPromise = null
  }

  /**
   * Check if we have a valid cached token
   */
  hasValidToken() {
    return this.cachedToken && this.tokenExpiry && Date.now() < this.tokenExpiry
  }

  /**
   * Force a token refresh in a single-flight manner.
   * - Concurrent callers await the same refreshPromise.
   * - On success, caches the new token and returns it.
   * - On failure, clears cache so next getToken can attempt again.
   */
  async refreshToken(options = {}) {
    // If a refresh is already in progress, wait for it
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    // Ensure future calls do not use stale token
    this.clearToken()

    this.refreshPromise = (async () => {
      try {
        // Bypass cache by directly fetching a new token
        const token = await this._fetchToken({ ...options, ignoreCache: true })
        return token
      } finally {
        // Always clear the refreshPromise so future refreshes can occur
        this.refreshPromise = null
      }
    })()

    return this.refreshPromise
  }
}

// Create singleton instance
const tokenManager = new TokenManager()

export default tokenManager
